import type { TabsStateInterface } from '@hypercodestudio/basler-components';
import waitForElement from '~/utils/waitForElement';

export const TAB_PANEL_HASH_PREFIX = '#tab-panel-';
export const TAB_ITEM_HASH_PREFIX = '#tab-item-';

/**
 * `TabsComponent` renders both ids for every tab: `tab-item-<id>` on the button
 * in the tab list and `tab-panel-<id>` on the content. Editors pick either one
 * when they copy an anchor out of the DOM, so both address the same tab.
 */
const TAB_HASH_PREFIXES = [TAB_PANEL_HASH_PREFIX, TAB_ITEM_HASH_PREFIX];

/**
 * Reads the tab id out of a `#tab-panel-<tab-id>` or `#tab-item-<tab-id>`
 * fragment.
 */
export function parseTabIdFromHash(hash: string): string | undefined {
  const prefix = TAB_HASH_PREFIXES.find((candidate) =>
    hash.startsWith(candidate)
  );

  if (!prefix) {
    return undefined;
  }

  return hash.slice(prefix.length) || undefined;
}

interface TabRequest {
  tabId: string;
  seq: number;
}

interface UsePdpTabDeepLinkOptions {
  /** External `TabsComponent` state holding the registered `TabPanel`s. */
  tabsState: TabsStateInterface;
  /** Keeps `usePdpActiveTab` in sync, since we bypass the `tab-change` event. */
  onActivate: (tabId: string) => void;
}

/**
 * Opens the PDP tab addressed by a `#tab-panel-<tab-id>` or `#tab-item-<tab-id>`
 * fragment and scrolls to it. Tabs register on mount and enrichment/download
 * tabs arrive asynchronously, so the request is kept until the matching panel
 * exists. A tab that never shows up simply leaves the default selection
 * untouched (DCSERVICE-3949).
 */
export function usePdpTabDeepLink({
  tabsState,
  onActivate
}: UsePdpTabDeepLinkOptions): void {
  const route = useRoute();
  const request = ref<TabRequest | undefined>();
  let requestCounter = 0;
  let handledSeq = 0;

  const registeredTabIds = computed(() =>
    tabsState.tabs.map((tab) => tab.computedId)
  );

  async function activateTab(tabId: string, scroll: boolean): Promise<void> {
    tabsState.tabs.forEach((tab) => {
      tab.isActive = tab.computedId === tabId;
    });
    onActivate(tabId);

    if (!scroll) {
      return;
    }

    // Inactive panels are hidden via `v-show`, so the browser cannot jump there
    // on its own; scroll only once the panel became visible.
    await nextTick();
    const panel = await waitForElement(`tab-panel-${tabId}`);
    panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function requestTabFromHash(hash: string): void {
    const tabId = parseTabIdFromHash(hash);
    if (tabId) {
      request.value = { tabId, seq: ++requestCounter };
    }
  }

  function handleHashChange(): void {
    requestTabFromHash(window.location.hash);
  }

  /**
   * Clicking an anchor whose fragment already matches the current URL does not
   * emit `hashchange`, so in-page links would stop working after the user
   * switched tabs manually. The native jump is left intact to keep the URL
   * shareable.
   */
  function handleDocumentClick(event: MouseEvent): void {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const anchor =
      event.target instanceof Element ? event.target.closest('a') : null;
    const href = anchor?.getAttribute('href');
    if (!href) {
      return;
    }

    const hashIndex = href.indexOf('#');
    if (hashIndex === -1) {
      return;
    }

    requestTabFromHash(href.slice(hashIndex));
  }

  watch(
    [request, registeredTabIds],
    ([currentRequest, tabIds]) => {
      if (!currentRequest || !tabIds.includes(currentRequest.tabId)) {
        return;
      }

      const isNewRequest = currentRequest.seq !== handledSeq;
      const isActive = tabsState.tabs.some(
        (tab) => tab.computedId === currentRequest.tabId && tab.isActive
      );

      // Re-apply silently when a `TabsSection` remount reset the selection to
      // the first tab, but never scroll the user around a second time.
      if (!isNewRequest && isActive) {
        return;
      }

      handledSeq = currentRequest.seq;
      void activateTab(currentRequest.tabId, isNewRequest);
    },
    { flush: 'post' }
  );

  onMounted(() => {
    requestTabFromHash(route.hash);
    window.addEventListener('hashchange', handleHashChange);
    document.addEventListener('click', handleDocumentClick);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('hashchange', handleHashChange);
    document.removeEventListener('click', handleDocumentClick);
  });
}
